"use client";
import { ErrorPageContent } from "@components/errors/ErrorPageContent";
import { DocumentHead } from "@templates/document/DocumentHead";
import { SvgSprite } from "@templates/document/SvgSprite";
import GlobalScripts from "@lib/scripts/GlobalScripts";
import { SetScrollbarWidthVariable } from "@components/ui/set-scrollbar-width-variable/SetScrollbarWidthVariable";
import { GlobalCss } from "@templates/document/GlobalCss";
import { routing } from "@i18n/routing";
import { ViewportMetaClient } from "@lib/scripts/ViewportMetaClient";
import type { Viewport } from "next";
import { useEffect } from "react";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import {
    clearRedirectAttemptMarker,
    isNavigationAbortError,
    useNavigationAbortRecovery,
} from "@lib/monitoring/navigationAbortError";
import HeadScripts from "@lib/scripts/HeadScript";

export const viewport: Viewport = {
    width: "device-width",
    initialScale: 1,
    maximumScale: 5,
};

/*
Because global-error.tsx is the final layer of error handling we don't try to fetch any data here.
This template should be treated as a last resort when all else fails and as such should be kept as slim as possible.
It will not use any layout so we must include html, head and body tags here.

This is also where a harmless Safari error can land right after a redirect payment (Klarna,
Przelewy24) is triggered. See @lib/monitoring/navigationAbortError for the check itself.

Why we hide it instead of fixing it: Safari cancels a fetch() started internally the instant the
redirect navigates the browser away, and that's a WebKit/Safari bug, not something in our code
that we can fix or prevent. Apple's own developer forums document this exact behavior:
https://developer.apple.com/forums/thread/771127
*/

export default function ErrorPage({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    const suppressAsNavigationAbort = isNavigationAbortError(error);

    useEffect(() => {
        if (suppressAsNavigationAbort) {
            clearRedirectAttemptMarker();
            // Log as info, not error: this isn't a real failure.
            Logger.info(ServiceType.WEB, "Global error page (Suppressed: Navigation Abort)", {
                tag: LogTag.ERROR,
                error: {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                },
                digest: error.digest,
            });
        } else {
            Logger.error(ServiceType.WEB, "Global error page", {
                tag: LogTag.ERROR,
                error,
                digest: error.digest,
            });
        }
    }, [error, suppressAsNavigationAbort]);

    useNavigationAbortRecovery(suppressAsNavigationAbort, reset);

    const locale = routing.defaultLocale;
    return (
        <html data-theme="light" style={{ colorScheme: "light" }} lang={locale}>
            <head>
                <DocumentHead />
                <HeadScripts featureFlags={{}} />
            </head>
            <body
                style={{ overflowX: "hidden" }}
                // We're using light as default without detecting system.
                // Setting it initially avoid a little flicker.
                className="chakra-ui-light"
            >
                <GlobalCss />
                <SvgSprite />
                <ViewportMetaClient />
                <GlobalScripts locale={locale} featureFlags={{}} />
                <SetScrollbarWidthVariable />
                {/* Skip showing the error page for the harmless redirect case, see above. */}
                {!suppressAsNavigationAbort && <ErrorPageContent errorCode={500} />}
            </body>
        </html>
    );
}
